import React from "react";
import PolicyLayout from "./PolicyLayout";
import "../css/policy.scss";

const sections = [
  {
    id: "scope-controller",
    title: "Scope and who is responsible",
    body: [
      "This notice explains how the operator of nartvmora handles personal information when a person visits this storefront, places or discusses an order, requests a return, makes a privacy request or otherwise contacts us.",
      "For questions or requests, email service@nartvmora.site. This notice does not govern an independent website or service linked from nartvmora, including a hosted payment page, which provides its own privacy information.",
    ],
  },
  {
    id: "information-collected",
    title: "Information we may collect",
    table: [
      ["Identity and contact", "Name, email address, delivery details and any company name supplied at checkout."],
      ["Order and service", "Products, size, colour, quantity, order reference, value, delivery status, returns, refunds and customer-care messages."],
      ["Payment-related", "Payment status, method type, transaction reference and fraud indicators supplied by the payment provider; not the raw card number or security code."],
      ["Device and use", "IP address, browser and device information, requested pages, timestamps, security events and similar server or diagnostic records."],
      ["Local browser data", "Cart contents and a limited recent-order summary stored on the visitor's device."],
      ["Submitted content", "Photographs, documents, feedback or other information a person chooses to provide for a support or product claim."],
    ],
  },
  {
    id: "sources",
    title: "Where information comes from",
    body: [
      "Most information comes directly from the person who shops or contacts us. We may also receive transaction status from a payment provider, delivery events from a carrier, fraud or security signals from service providers, and information from an authorised representative.",
      "The storefront does not intentionally collect sensitive personal information. Please do not send payment credentials, government identifiers or other sensitive material unless it is specifically and lawfully required for a request.",
    ],
  },
  {
    id: "purposes-bases",
    title: "Why information is used",
    items: [
      "To take steps requested before a purchase and to accept, fulfil, deliver, return and refund an order.",
      "To answer questions, provide customer care and manage product or consumer-guarantee claims.",
      "To authenticate transactions, protect customers and the service, prevent fraud and investigate misuse.",
      "To operate, troubleshoot and improve website performance, accessibility and the shopping journey.",
      "To keep financial, tax, delivery and dispute records required by law or reasonably needed to establish, exercise or defend legal claims.",
      "To comply with a legal obligation, enforce site terms and protect vital or public interests where applicable.",
    ],
    body: [
      "The legal basis depends on the location and context. It may be performance of a contract, steps requested before a contract, a legal obligation, legitimate interests that do not override individual rights, or consent where consent is required.",
    ],
  },
  {
    id: "payment",
    title: "Payment processing",
    body: [
      "Checkout redirects to an independent hosted payment page. Card numbers, payment-account passwords and card security codes are entered with the payment provider and are not stored in this storefront.",
      "We send limited transaction data needed to create the payment request, such as an order reference, amount, currency and product description. We receive the result and other limited records needed to administer the order, refunds and disputes.",
    ],
    links: [{ to: "/payments", label: "Payments & billing" }],
  },
  {
    id: "local-storage",
    title: "Local storage and cookies",
    body: [
      "The storefront uses browser local storage to retain cart selections and a limited recent-order summary on the visitor's device. Server, security and payment services may use cookies or similar technologies when needed to provide their functions.",
      "The current storefront does not intentionally enable third-party behavioural advertising. See the Cookie Policy for the current browser-storage details and controls.",
    ],
    links: [{ to: "/cookies", label: "Cookie policy" }],
  },
  {
    id: "sharing",
    title: "When information may be disclosed",
    items: [
      "Payment processors and payment-routing providers, to request and confirm a transaction.",
      "Hosting, security, infrastructure and technical-support providers, to operate and protect the service.",
      "Warehouses, fulfilment partners and delivery carriers, to prepare, deliver and recover parcels.",
      "Customer-care, returns, professional, audit, insurance or legal advisers where reasonably necessary.",
      "Authorities, courts or other parties where disclosure is required by law or reasonably necessary to protect rights, safety, customers or the service.",
      "A buyer, successor or adviser in a genuine business transaction, subject to appropriate confidentiality and legal safeguards.",
    ],
    body: [
      "Service providers should receive only the information reasonably needed for their role and must be subject to appropriate use, confidentiality and security restrictions. nartvmora does not intentionally sell personal information or share it for cross-context behavioural advertising through the current storefront.",
    ],
  },
  {
    id: "international",
    title: "International processing",
    body: [
      "Online retail providers may operate in countries other than the customer's own. Where a restricted international transfer occurs, we will use a lawful transfer mechanism or another safeguard required for the affected information, and provide further details where the law requires it.",
    ],
  },
  {
    id: "retention",
    title: "How long information is kept",
    body: [
      "Retention depends on the purpose, sensitivity, legal requirements and whether a record is needed for an order, refund, fraud review or dispute. We delete or anonymise information when it is no longer reasonably needed, subject to required backup cycles.",
    ],
    table: [
      ["Cart and recent order on device", "Until the visitor clears site data, replaces it through later use or the browser removes it."],
      ["Order, payment and tax records", "For the period required by applicable financial, tax, consumer and limitation laws."],
      ["Customer-care and claim records", "For the request and a reasonable follow-up period, or longer if a dispute or legal duty requires it."],
      ["Security and diagnostic records", "For a limited period proportionate to security, troubleshooting and fraud-prevention needs."],
    ],
  },
  {
    id: "security",
    title: "Security",
    body: [
      "We use reasonable technical and organisational measures appropriate to the information and service, including limiting access and using specialist payment processing. No internet transmission or storage system can be guaranteed completely secure.",
      "Anyone who believes personal information or an order has been accessed without authority should contact service@nartvmora.site promptly and avoid sending passwords or full card details.",
    ],
  },
  {
    id: "rights",
    title: "Privacy rights and choices",
    body: [
      "Depending on location, a person may have rights to access, know about, correct, delete, restrict or object to processing, obtain a portable copy, withdraw consent, appeal a decision, or complain to a privacy authority. Exceptions may apply, and identity may need to be verified proportionately.",
      "The Your Privacy Choices page explains how to make a request. Browser settings can be used to remove local cart and recent-order data.",
    ],
    links: [{ to: "/privacy-choices", label: "Your privacy choices" }],
  },
  {
    id: "children",
    title: "Children",
    body: [
      "The storefront is intended for people who can lawfully enter a purchase contract and is not directed to children. We do not knowingly request personal information from a child for independent use of the service. A parent or guardian who believes a child has provided information should contact us so the situation can be reviewed.",
    ],
  },
  {
    id: "updates-contact",
    title: "Changes and contact",
    body: [
      "This notice may be updated when the website, providers or legal obligations change. The effective date will be revised, and a more prominent notice will be used where a change materially affects people and the law requires it.",
      "Privacy enquiries and requests can be sent to service@nartvmora.site.",
    ],
    links: [
      { to: "/contact", label: "Contact us" },
      { to: "/policies", label: "All policies" },
    ],
  },
];

function Privacy() {
  return (
    <PolicyLayout
      eyebrow="Legal"
      title="Privacy Notice"
      intro="How nartvmora collects, uses, discloses and protects personal information."
      updated="Effective 16 October 2026"
      sections={sections}
      action={{ to: "/privacy-choices", label: "Review your privacy choices" }}
    />
  );
}

export default Privacy;
